import React, {ChangeEvent, useCallback, useRef, useState} from "react";
import {
    TextTranslatorCard,
    TextTranslatorCardHeader,
    TextTranslatorCardHeaderActionButtonContainer,
    TextTranslatorCardHeaderContainer,
    TextTranslatorCardMain,
    TextTranslatorCardMainContainer,
    TextTranslatorCardMainSeparator,
    TextTranslatorCardMainSourceFooter,
    TextTranslatorCardMainSourceFooterCharacterCounter,
    TextTranslatorCardMainSourceTextarea,
    TextTranslatorCardMainSourceTextareaContainer,
    TextTranslatorCardMainTargetFooter,
    TextTranslatorCardMainTargetTextarea,
    TextTranslatorCardMainTargetTextareaContainer
} from "./TextTranslator.styled";
import LanguageSwitcher from "../../../../components/common/language/LanguageSwitcher";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {CHAT_CONSTANTS} from "../../../../config/chat.constants";
import {languages} from "../../../../config/languages";
import {useTextTranslationContext} from "../../../../contexts/TextTranslationContext";
import {useTranslation} from "react-i18next";
import {Button} from "@mui/material";

const TextTranslator = () => {
    const targetTextareaRef = useRef<HTMLTextAreaElement>(null);

    const {t} = useTranslation();
    const {
        translatedText,
        isStreaming,
        sourceLanguage,
        targetLanguage,
        setSourceLanguage,
        setTargetLanguage,
        startTranslation,
        clearTranslation
    } = useTextTranslationContext();

    const [sourceText, setSourceText] = useState('');
    const languageList = languages.filter(lang => lang.forTranslator);

    const handleSourceLanguageOnChange = (languageCode: string) => {
        setSourceLanguage(languageCode);
    };

    const handleTargetLanguageOnChange = (languageCode: string) => {
        setTargetLanguage(languageCode);
    };

    const handleSourceTextareaOnChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setSourceText(event.target.value);
    };

    const handleTranslate = () => {
        if (!sourceText.trim() || isStreaming) return;

        startTranslation(sourceText);
    };

    const handleClear = () => {
        setSourceText('');
        clearTranslation();
    }

    const copyToClipboard = useCallback((text: string): void => {
        void navigator.clipboard.writeText(text);
    }, []);

    return (
        <TextTranslatorCard>
            <TextTranslatorCardHeader>
                <TextTranslatorCardHeaderContainer>
                    <LanguageSwitcher
                        languageList={languageList}
                        sourceLanguageCode={sourceLanguage}
                        targetLanguageCode={targetLanguage}
                        sourceOnChange={handleSourceLanguageOnChange}
                        targetOnChange={handleTargetLanguageOnChange}
                    />

                    <TextTranslatorCardHeaderActionButtonContainer>
                        <Button
                            variant='contained'
                            onClick={handleTranslate}
                        >
                            {t('textTranslator.translate')}
                        </Button>

                        <Button
                            variant={'text'}
                            onClick={handleClear}
                        >
                            {t('textTranslator.clear')}
                        </Button>
                    </TextTranslatorCardHeaderActionButtonContainer>

                </TextTranslatorCardHeaderContainer>
            </TextTranslatorCardHeader>

            <TextTranslatorCardMain>
                <TextTranslatorCardMainContainer>

                    <TextTranslatorCardMainSourceTextareaContainer>
                        <TextTranslatorCardMainSourceTextarea
                            maxLength={CHAT_CONSTANTS.MAX_MESSAGE_LENGTH}
                            value={sourceText}
                            onChange={handleSourceTextareaOnChange}
                        />

                        <TextTranslatorCardMainSourceFooter>
                            <TextTranslatorCardMainSourceFooterCharacterCounter>
                                {sourceText.length}/{CHAT_CONSTANTS.MAX_MESSAGE_LENGTH} {t('textTranslator.char')}
                            </TextTranslatorCardMainSourceFooterCharacterCounter>
                        </TextTranslatorCardMainSourceFooter>
                    </TextTranslatorCardMainSourceTextareaContainer>

                    <TextTranslatorCardMainSeparator/>

                    <TextTranslatorCardMainTargetTextareaContainer>
                        <TextTranslatorCardMainTargetTextarea
                            ref={targetTextareaRef}
                            readOnly={true}
                            tabIndex={-1}
                            value={translatedText}
                        />
                        <TextTranslatorCardMainTargetFooter>
                            <Button
                                variant='outlined'
                                onClick={() => copyToClipboard(translatedText)}
                                style={{
                                    marginRight: '24px',
                                    padding: '6px 12px'
                                }}
                                startIcon={<ContentCopyIcon />}
                            >
                                {t('textTranslator.copy')}
                            </Button>

                        </TextTranslatorCardMainTargetFooter>
                    </TextTranslatorCardMainTargetTextareaContainer>

                </TextTranslatorCardMainContainer>
            </TextTranslatorCardMain>
        </TextTranslatorCard>
    );
}

export default TextTranslator;